import { useCallback, useEffect, useState } from 'react';
import {
  getChavePush, salvarInscricaoPush, removerInscricaoPush, enviarPushDeTeste,
} from '../services/api';

/**
 * Ligar e desligar os avisos no celular.
 *
 * ── A permissão só se pede UMA vez ────────────────────────────────────────
 *
 * Negada, o navegador não deixa perguntar de novo — a pessoa teria que ir nas
 * configurações do sistema. Por isso o pedido nasce de um TOQUE dela num
 * interruptor, e não de um modal que aparece sozinho: a chance de negar por
 * reflexo é a menor possível quando ela acabou de pedir.
 *
 * ── Por que o service worker é registrado sob demanda ────────────────────
 *
 * Ele existe só para receber notificação. Registrar no boot poria um worker no
 * aparelho de todo mundo, inclusive de quem nunca vai ligar os avisos — e um
 * service worker instalado é uma peça a mais que pode dar errado, num app que
 * mostra saldo.
 *
 * Aqui ele entra quando ela liga, e some quando ela desliga.
 */

export type EstadoDosAvisos =
  | 'indisponivel'    // o navegador não faz push (o Safari fora da tela inicial)
  | 'servidor-sem-chave' // o push existe, mas as chaves VAPID não foram postas
  | 'desligado'
  | 'ligado'
  | 'bloqueado';      // ela negou; só as configurações do sistema revertem

const suportado = (): boolean =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

/**
 * Base64 URL-safe para bytes.
 *
 * `applicationServerKey` exige um `Uint8Array`; a chave VAPID viaja como texto.
 * A conversão é chata e é sempre a mesma — está aqui para não ser reescrita
 * errada em outro lugar.
 */
const paraBytes = (base64: string): Uint8Array => {
  const preenchido = (base64 + '='.repeat((4 - base64.length % 4) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const cru = window.atob(preenchido);
  return Uint8Array.from(cru, c => c.charCodeAt(0));
};

const registrar = () => navigator.serviceWorker.register('/sw.js');

export const useAvisosNoCelular = () => {
  const [estado, setEstado] = useState<EstadoDosAvisos>('indisponivel');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!suportado()) return;
      if (Notification.permission === 'denied') {
        if (vivo) setEstado('bloqueado');
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const inscricao = reg ? await reg.pushManager.getSubscription() : null;
        if (vivo) setEstado(inscricao ? 'ligado' : 'desligado');

        /*
         * ── O reenvio, e o silêncio que ele conserta ────────────────────────
         *
         * O estado desta tela vinha SÓ do navegador. Ele diz se o aparelho tem
         * inscrição — não diz se o SERVIDOR sabe dela, e são duas coisas que
         * se separam:
         *
         *   resetar a conta apaga `push_subscriptions` no servidor, e o
         *   navegador continua inscrito;
         *
         *   o POST que grava a inscrição pode ter falhado uma vez, na rede.
         *
         * Nos dois casos a tela dizia "ligado" e mostrava o horário do aviso,
         * enquanto o servidor não tinha para quem mandar. A pessoa só descobre
         * pelo que NÃO chega — e "não chegou" é indistinguível de "não era
         * hora ainda".
         *
         * Foi assim que ela ficou sem o aviso das 13h: zero inscrições no
         * banco, e a tela prometendo.
         *
         * O reenvio é idempotente (`ON CONFLICT (endpoint) DO UPDATE`), então
         * mandar de novo a cada carga custa um upsert e conserta os dois casos
         * sozinho. Falhar aqui não muda nada na tela: quem manda no que a
         * pessoa vê é a inscrição do aparelho, e ela continua lá.
         */
        if (inscricao) {
          salvarInscricaoPush(inscricao.toJSON()).catch(() => {});
        }
      } catch {
        if (vivo) setEstado('desligado');
      }
    })();
    return () => { vivo = false; };
  }, []);

  /**
   * `testeSaiu` é `null` até ela ligar, e depois diz se o servidor conseguiu
   * entregar em algum aparelho.
   *
   * Não é o mesmo que "a notificação apareceu": o canal do sistema pode estar
   * silencioso, e disso o servidor não sabe. É por isso que o modal seguinte
   * ensina onde ajustar em vez de afirmar que chegou.
   */
  const [testeSaiu, setTesteSaiu] = useState<boolean | null>(null);

  const ligar = useCallback(async (): Promise<EstadoDosAvisos> => {
    setOcupado(true);
    try {
      const chave = await getChavePush();
      /*
       * Sem chave no servidor não há push — e isso é DIFERENTE de o navegador
       * não fazer push.
       *
       * A primeira versão usava 'indisponivel' para os dois, e o item some da
       * tela quando o estado é esse: tocar fazia o botão desaparecer sem
       * explicação. Quem cuida do app é a mesma pessoa que o usa, e o que ela
       * precisa ler ali é "falta configurar", não o nada.
       *
       * A checagem é no TOQUE e não na montagem: perguntar ao servidor a cada
       * carga do app custaria uma requisição para todo mundo por causa de uma
       * configuração que muda uma vez na vida.
       */
      if (!chave) { setEstado('servidor-sem-chave'); return 'servidor-sem-chave'; }

      const permissao = await Notification.requestPermission();
      if (permissao !== 'granted') {
        const novo = permissao === 'denied' ? 'bloqueado' : 'desligado';
        setEstado(novo);
        return novo;
      }

      const reg = await registrar();
      // `ready` porque o worker recém-registrado ainda não está ativo, e
      // `subscribe` num worker inativo falha.
      await navigator.serviceWorker.ready;

      const inscricao = await reg.pushManager.subscribe({
        // Push silencioso não existe para a web: o navegador exige que TODA
        // mensagem vire notificação visível. É a regra que impede rastreamento
        // por push, e nós não temos motivo para querer o contrário.
        userVisibleOnly: true,
        applicationServerKey: paraBytes(chave),
      });

      await salvarInscricaoPush(inscricao.toJSON());
      setEstado('ligado');

      /*
       * Um aviso de teste na hora.
       *
       * Sem ele, a única forma de saber se funciona é esperar um alerta de
       * verdade — que pode levar dias, e que quando não chega não diz se o
       * problema foi a permissão, a chave, a inscrição ou a entrega.
       *
       * O `catch` está dentro de `enviarPushDeTeste`: falhar o teste NÃO pode
       * desfazer a inscrição, que já foi gravada e é o que importa. No pior
       * caso ela ligou e não viu a confirmação.
       */
      setTesteSaiu(await enviarPushDeTeste());
      return 'ligado';
    } catch {
      setEstado('desligado');
      return 'desligado';
    } finally {
      setOcupado(false);
    }
  }, []);

  const desligar = useCallback(async () => {
    setOcupado(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const inscricao = reg ? await reg.pushManager.getSubscription() : null;
      /*
       * A ordem: avisa o servidor ANTES de cancelar no navegador.
       *
       * Invertido, uma falha de rede deixaria a inscrição morta no banco — e a
       * escada de canais continuaria escolhendo push para um aparelho que não
       * recebe mais nada. Ela pararia de ser avisada, em silêncio.
       */
      if (inscricao) {
        await removerInscricaoPush(inscricao.endpoint);
        await inscricao.unsubscribe();
      }
      // O worker sai junto: ele só existia para isto.
      if (reg) await reg.unregister();
      setEstado('desligado');
    } finally {
      setOcupado(false);
    }
  }, []);

  return { estado, ocupado, ligar, desligar, testeSaiu, limparTeste: () => setTesteSaiu(null) };
};
